import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const lines = cartItems
    .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
    .filter((item) => item.product);
  const subtotal = lines.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = subtotal >= siteConfig.shippingThreshold ? 0 : siteConfig.standardShipping;
  const remaining = Math.max(0, siteConfig.shippingThreshold - subtotal);

  if (lines.length === 0) {
    return (
      <main className="cart-empty page-shell">
        <span className="eyebrow">Your bag · 0 items</span>
        <h1>You haven't added any items yet</h1>
        <p>Explore heavyweight denim, enduring shirts and road-ready layers.</p>
        <Link className="button-primary" to="/shop">Continue shopping</Link>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-page__header page-shell">
        <span className="eyebrow">Review your order</span>
        <h1>Your cart</h1>
        <span>{lines.reduce((sum, item) => sum + item.quantity, 0)} items</span>
      </div>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Cart items">
          {lines.map((item) => (
            <article className="cart-line" key={`${item.id}-${item.size}-${item.color}`}>
              <Link className="cart-line__image" to={`/shopDetails/${item.product.id}`}>
                <img src={item.product.image} alt={item.product.name} />
              </Link>
              <div className="cart-line__details">
                <span className="eyebrow">{item.product.sku}</span>
                <Link to={`/shopDetails/${item.product.id}`}><h2>{item.product.name}</h2></Link>
                <p>{item.color} · {item.size}</p>
                <strong>{formatPrice(item.product.price)}</strong>
              </div>
              <div className="cart-line__controls">
                <div className="quantity-control" aria-label={`Quantity for ${item.product.name}`}>
                  <button type="button" onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity - 1)} aria-label="Decrease quantity"><FiMinus /></button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)}
                    aria-label="Increase quantity"
                    disabled={item.quantity >= item.product.stock}
                  ><FiPlus /></button>
                </div>
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                <button className="cart-line__remove" type="button" onClick={() => removeFromCart(item.id, item.size, item.color)} aria-label={`Remove ${item.product.name}`}><FiTrash2 /></button>
              </div>
            </article>
          ))}
          <Link className="cart-page__continue" to="/shop">← Continue shopping</Link>
        </section>

        <aside className="cart-summary">
          <span className="eyebrow">Order summary</span>
          <h2>Total</h2>
          <div className="shipping-progress">
            <p>{remaining > 0 ? `Spend ${formatPrice(remaining)} more for free shipping.` : "You've unlocked free shipping."}</p>
            <span><i style={{ width: `${Math.min(100, (subtotal / siteConfig.shippingThreshold) * 100)}%` }} /></span>
          </div>
          <dl>
            <div><dt>Items</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Shipping</dt><dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd></div>
            <div className="cart-summary__total"><dt>Total</dt><dd>{formatPrice(subtotal + shipping)}</dd></div>
          </dl>
          <p>Taxes are included. Shipping is calculated for the selected service at checkout.</p>
          <button className="button-primary" type="button" onClick={() => navigate("/checkout")}>Checkout securely</button>
          <div className="cart-summary__notes"><span>28 day returns</span><span>Tracked delivery</span></div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
